import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, HeartPulse, BookOpenText, MoonStar, Flower2 } from "lucide-react";
import BrandLogo from "./BrandLogo";

/** Swift, beautiful 4-screen welcome carousel.
 *  Shown ONCE before the existing 3-step profile onboarding.
 *  Pure CSS-only illustration (no asset dependencies).
 */
const SLIDES = [
  {
    eyebrow: "Welcome to Knya",
    title: "Your body, gently translated.",
    sub: "Knya is a calm space that learns your rhythm and quietly turns it into intelligence.",
    Icon: Flower2,
    accent: "bg-primary",
    glow: "bg-primary/30",
  },
  {
    eyebrow: "Cycle",
    title: "Track without the noise.",
    sub: "Periods, phases, symptoms, pregnancy — one warm thread, never a checklist.",
    Icon: HeartPulse,
    accent: "bg-secondary",
    glow: "bg-secondary/30",
  },
  {
    eyebrow: "Wellness",
    title: "Move, breathe, hydrate.",
    sub: "Soft daily rings for the things that matter — movement, mind, and water. No pressure.",
    Icon: MoonStar,
    accent: "bg-aquamarine",
    glow: "bg-aquamarine-soft",
  },
  {
    eyebrow: "Coach",
    title: "An AI that listens first.",
    sub: "Ask anything you'd rather not Google. Knya speaks in 2–3 sentences — warm, never clinical.",
    Icon: Sparkles,
    accent: "bg-primary",
    glow: "bg-primary/25",
  },
];

export default function WelcomeCarousel({ onDone }) {
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const last = idx === SLIDES.length - 1;

  const next = () => {
    if (last) onDone?.();
    else setIdx((i) => i + 1);
  };
  const skip = () => onDone?.();

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" data-testid="welcome-carousel">
      {/* Brand-tinted ambient background */}
      <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-hero)" }} />
      <div className={`absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl -z-10 transition-colors duration-500 ${slide.glow}`} />
      <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-secondary/15 blur-3xl -z-10" />

      {/* Top bar: brand mark + Skip */}
      <div className="flex items-center justify-between px-6 pt-12 relative z-10">
        <div className="flex items-center gap-2">
          <BrandLogo size={36} />
          <span className="font-extrabold text-lg tracking-tight">Knya</span>
        </div>
        {!last && (
          <button onClick={skip} className="text-[11px] uppercase tracking-wider font-extrabold text-muted-foreground hover:text-foreground transition-colors" data-testid="welcome-skip">
            Skip
          </button>
        )}
      </div>

      {/* Hero illustration disc */}
      <div className="flex-1 flex items-center justify-center px-6 pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.96 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="w-full max-w-[340px] text-center"
          >
            <div className="relative mx-auto h-52 w-52 rounded-[44px] bg-card border border-border shadow-card flex items-center justify-center overflow-hidden">
              {/* Animated glow — pulses on slide 0 */}
              <motion.div
                className={`absolute inset-3 rounded-[36px] ${slide.glow} blur-md`}
                animate={idx === 0
                  ? { opacity: [0.5, 1, 0.5], scale: [1, 1.06, 1] }
                  : { opacity: 0.7, scale: 1 }}
                transition={idx === 0
                  ? { duration: 2.8, repeat: Infinity, ease: "easeInOut" }
                  : { duration: 0.3 }}
              />

              {idx === 0 ? (
                /* Entrance spring → continuous float */
                <motion.div
                  initial={{ scale: 0.72, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  <motion.img
                    src="/knya-icon.png"
                    alt="Knya"
                    className="relative w-44 h-44 object-contain drop-shadow-xl"
                    draggable={false}
                    animate={{ y: [0, -9, 0] }}
                    transition={{
                      duration: 3.2,
                      ease: "easeInOut",
                      repeat: Infinity,
                      repeatType: "loop",
                      delay: 0.55,
                    }}
                  />
                </motion.div>
              ) : (
                <div className={`relative h-24 w-24 rounded-3xl ${slide.accent} text-white flex items-center justify-center shadow-soft`}>
                  <slide.Icon size={42} strokeWidth={2} />
                </div>
              )}

              <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-soft">
                <Sparkles size={13} />
              </span>
              <span className="absolute -bottom-2 -left-2 h-6 w-6 rounded-full bg-aquamarine" />
            </div>

            <p className="mt-7 text-[10px] uppercase tracking-wider text-primary font-extrabold">{slide.eyebrow}</p>
            <h1 className="mt-2 text-[30px] leading-[1.04] font-extrabold tracking-tight">{slide.title}</h1>
            <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed font-medium">{slide.sub}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Pagination + CTA */}
      <div className="px-6 pb-10 relative z-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 36px)" }}>
        <div className="flex items-center justify-center gap-1.5 mb-5" data-testid="welcome-pagination">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-primary" : "w-1.5 bg-foreground/20 hover:bg-foreground/40"}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`welcome-dot-${i}`}
            />
          ))}
        </div>
        <button
          onClick={next}
          className="w-full h-[54px] rounded-full bg-secondary text-secondary-foreground flex items-center justify-center gap-2 font-bold text-[15px] transition-all hover:bg-indigo-deep hover:shadow-[0_14px_30px_-12px_hsl(195_50%_18%/0.5)] active:scale-[0.99]"
          data-testid={last ? "welcome-done" : "welcome-next"}
        >
          {last ? "Begin" : "Next"} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
